import React from "react";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { tokens } from "@/tokens";

interface StatItem {
  label: string;
  value: string | number;
  color?: string;
  prefix?: string;
  centerValue?: boolean;
}

interface StatsBarProps {
  items: StatItem[];
  isLoading?: boolean;
  align?: "left" | "right" | "spread";
  compact?: boolean;
}

const Divider: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <Box
    sx={{
      width: "1px",
      height: compact ? "20px" : "28px",
      bgcolor: tokens.bg.border,
      flexShrink: 0,
    }}
  />
);

const StatsBar: React.FC<StatsBarProps> = ({ items, isLoading, align = "left", compact }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={compact ? 3 : 4}
      sx={{
        ...(align === "right" && { ml: "auto" }),
        ...(align === "spread" && { flex: 1, justifyContent: "space-evenly" }),
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 && <Divider compact={compact} />}
          <Box sx={{ minWidth: 0, ...(item.centerValue && { textAlign: "center" }) }}>
            <Typography
              sx={{
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.semibold,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: tokens.text.muted,
                lineHeight: "16px",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Typography>
            {isLoading ? (
              <Skeleton width={compact ? 40 : 56} height={compact ? 18 : 24} />
            ) : (
              <Typography
                sx={{
                  fontSize: compact ? "1.05rem" : "1.25rem",
                  fontWeight: tokens.fontWeight.bold,
                  color: item.color || tokens.text.primary,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {item.prefix}
                {item.value}
              </Typography>
            )}
          </Box>
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default StatsBar;
